import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChatInterface } from "@/components/ChatInterface";
import { Button } from "@/components/ui/button";
import { Sparkles, Headphones, HeartHandshake, ArrowRight, BookOpen, Laptop, CalendarDays, ClipboardList, ShieldCheck, Bus, UtensilsCrossed, Languages, Newspaper, ArrowUpRight, HeartPulse, Scale, HandCoins, Phone, LifeBuoy, Building2, BadgeCheck, Lock, Clock, GraduationCap, PlayCircle } from "lucide-react";

const HISPANIC_IMG = "https://images.pexels.com/photos/7173469/pexels-photo-7173469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TUTOR_IMG = "https://images.unsplash.com/photo-1623076189461-f7706b741c04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxjaGlsZCUyMG9ubGluZSUyMHR1dG9yaW5nJTIwbGFwdG9wJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzg0ODE5NjI5fDA&ixlib=rb-4.1.0&q=85";

const EVENTS = {
  en: [
    { month: "AUG", day: "26", title: "First Day of School", desc: "Welcome back! Classes begin for all HISD students.", tag: "Calendar" },
    { month: "SEP", day: "02", title: "Labor Day Holiday", desc: "No school for students and staff.", tag: "Holiday" },
    { month: "SEP", day: "13", title: "Fall Enrollment Deadline", desc: "Last day to complete registration for new students.", tag: "Deadline" },
    { month: "SEP", day: "19", title: "Family Curriculum Night", desc: "Meet your child's teachers and learn about the year ahead.", tag: "Event" },
    { month: "OCT", day: "09", title: "Report Cards Issued", desc: "First grading period report cards go home to families.", tag: "Academics" },
  ],
  es: [
    { month: "AGO", day: "26", title: "Primer D\u00eda de Clases", desc: "\u00a1Bienvenidos! Comienzan las clases para todos los estudiantes de HISD.", tag: "Calendario" },
    { month: "SEP", day: "02", title: "D\u00eda del Trabajo", desc: "No hay clases para estudiantes ni personal.", tag: "Feriado" },
    { month: "SEP", day: "13", title: "Fecha L\u00edmite de Inscripci\u00f3n", desc: "\u00daltimo d\u00eda para completar el registro de nuevos estudiantes.", tag: "Fecha l\u00edmite" },
    { month: "SEP", day: "19", title: "Noche de Currículo Familiar", desc: "Conoce a los maestros de tu hijo y el plan del a\u00f1o.", tag: "Evento" },
    { month: "OCT", day: "09", title: "Entrega de Calificaciones", desc: "Las boletas del primer periodo llegan a las familias.", tag: "Acad\u00e9mico" },
  ],
};

const RESOURCES = {
  en: [
    { icon: ClipboardList, title: "Enrollment Checklist", desc: "Everything you need to register your child, step by step." },
    { icon: ShieldCheck, title: "Immunization Guide", desc: "Texas vaccine requirements and where to get free shots." },
    { icon: Bus, title: "Bus Route Finder", desc: "Find your child's stop, pickup times and eligibility." },
    { icon: UtensilsCrossed, title: "Free Meals Info", desc: "Free breakfast & lunch details and menus for every campus." },
    { icon: BookOpen, title: "Parent Portal Guide", desc: "Set up HISD Connect to track grades and attendance." },
    { icon: Languages, title: "Bilingual Support", desc: "Translation, ESL and dual-language program resources." },
  ],
  es: [
    { icon: ClipboardList, title: "Lista de Inscripci\u00f3n", desc: "Todo lo que necesitas para inscribir a tu hijo, paso a paso." },
    { icon: ShieldCheck, title: "Gu\u00eda de Vacunas", desc: "Requisitos de vacunas de Texas y d\u00f3nde vacunarse gratis." },
    { icon: Bus, title: "Buscador de Rutas", desc: "Encuentra la parada, horarios y elegibilidad de tu hijo." },
    { icon: UtensilsCrossed, title: "Comidas Gratis", desc: "Detalles de desayuno y almuerzo gratis y men\u00fas por escuela." },
    { icon: BookOpen, title: "Gu\u00eda del Portal", desc: "Configura HISD Connect para ver calificaciones y asistencia." },
    { icon: Languages, title: "Apoyo Biling\u00fce", desc: "Recursos de traducci\u00f3n, ESL y programas de doble idioma." },
  ],
};

const NEWS_IMG_1 = "https://images.unsplash.com/photo-1780061650486-2e8e3bec7e45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxzY2hvb2wlMjBzdHVkZW50cyUyMGNsYXNzcm9vbSUyMGNvbW11bml0eSUyMGV2ZW50fGVufDB8fHx8MTc4NDgyMDMxOHww&ixlib=rb-4.1.0&q=85";
const NEWS_IMG_2 = "https://images.pexels.com/photos/33415745/pexels-photo-33415745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const NEWS_IMG_3 = "https://images.pexels.com/photos/34162719/pexels-photo-34162719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const NEWS = {
  en: [
    { img: NEWS_IMG_1, tag: "Community", date: "Aug 20, 2025", title: "HISD Welcomes Families to a New School Year", excerpt: "Campuses across the district open their doors with welcome events, resource fairs and support for every family." },
    { img: NEWS_IMG_2, tag: "Students", date: "Aug 12, 2025", title: "Free Meals Continue for All Students District-Wide", excerpt: "Every enrolled HISD student receives free breakfast and lunch again this year \u2014 no application needed." },
    { img: NEWS_IMG_3, tag: "Programs", date: "Aug 05, 2025", title: "Dual-Language Programs Expand to More Campuses", excerpt: "More families can now enroll their children in bilingual and dual-language learning tracks this fall." },
  ],
  es: [
    { img: NEWS_IMG_1, tag: "Comunidad", date: "20 Ago 2025", title: "HISD Da la Bienvenida a las Familias al Nuevo A\u00f1o Escolar", excerpt: "Las escuelas del distrito abren sus puertas con eventos de bienvenida, ferias de recursos y apoyo para cada familia." },
    { img: NEWS_IMG_2, tag: "Estudiantes", date: "12 Ago 2025", title: "Las Comidas Gratis Contin\u00faan para Todos los Estudiantes", excerpt: "Cada estudiante inscrito en HISD recibe desayuno y almuerzo gratis de nuevo este a\u00f1o, sin necesidad de solicitud." },
    { img: NEWS_IMG_3, tag: "Programas", date: "05 Ago 2025", title: "Los Programas de Doble Idioma se Expanden a M\u00e1s Escuelas", excerpt: "M\u00e1s familias pueden inscribir a sus hijos en programas biling\u00fces y de doble idioma este oto\u00f1o." },
  ],
};

const COURSE_IMG_1 = "https://images.unsplash.com/photo-1613896527026-f195d5c818ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxzY2hvb2wlMjBidWlsZGluZyUyMGVkdWNhdGlvbiUyMHN5c3RlbXxlbnwwfHx8fDE3ODUyMTM4Njl8MA&ixlib=rb-4.1.0&q=85";
const COURSE_IMG_2 = "https://images.unsplash.com/photo-1577835724923-f591f5f98c89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjaGlsZCUyMHJlYWRpbmclMjBib29rJTIwYXQlMjBob21lfGVufDB8fHx8MTc4NTIxMzg2OXww&ixlib=rb-4.1.0&q=85";
const COURSE_IMG_3 = "https://images.unsplash.com/photo-1640622304233-7335e936f11b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxwYXJlbnQlMjB1c2luZyUyMGxhcHRvcCUyMGNvbXB1dGVyfGVufDB8fHx8MTc4NTIxMzg2OXww&ixlib=rb-4.1.0&q=85";
const COURSE_IMG_4 = "https://images.unsplash.com/photo-1640622302099-f10b18de7b16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBzYWZlJTIwbGFwdG9wfGVufDB8fHx8MTc4NTE4OTEzNXww&ixlib=rb-4.1.0&q=85";
const COURSE_IMG_5 = "https://images.unsplash.com/photo-1512238972088-8acb84db0771?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxhZHVsdCUyMGxhbmd1YWdlJTIwbGVhcm5pbmclMjBjbGFzc3xlbnwwfHx8fDE3ODUyMTM4Njl8MA&ixlib=rb-4.1.0&q=85";
const COURSE_IMG_6 = "https://images.unsplash.com/photo-1581998392741-67879e0ef04a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb3RoZXIlMjBjaGlsZCUyMGVtb3Rpb25hbCUyMHdlbGxiZWluZyUyMHN1cHBvcnR8ZW58MHx8fHwxNzg1MjEzODY5fDA&ixlib=rb-4.1.0&q=85";

const COURSES = {
  en: [
    { icon: GraduationCap, img: COURSE_IMG_1, title: "Understanding the U.S. School System", desc: "How grades, enrollment and school choice work in American public schools.", duration: "6 lessons" },
    { icon: BookOpen, img: COURSE_IMG_2, title: "Supporting Your Child's Reading", desc: "Simple daily habits to build strong reading skills at home.", duration: "4 lessons" },
    { icon: Laptop, img: COURSE_IMG_3, title: "Navigating the Parent Portal", desc: "Set up and use HISD Connect to track grades and attendance.", duration: "30 min" },
    { icon: ShieldCheck, img: COURSE_IMG_4, title: "Digital Safety for Families", desc: "Keep your children safe online with practical, easy tips.", duration: "5 lessons" },
    { icon: Languages, img: COURSE_IMG_5, title: "English for Parents (ESL Basics)", desc: "Everyday English to communicate with teachers and staff.", duration: "8 lessons" },
    { icon: HeartPulse, img: COURSE_IMG_6, title: "Supporting Your Child's Wellbeing", desc: "Recognize stress and nurture your child's mental health.", duration: "4 lessons" },
  ],
  es: [
    { icon: GraduationCap, img: COURSE_IMG_1, title: "Entendiendo el Sistema Escolar de EE.UU.", desc: "C\u00f3mo funcionan las calificaciones, la inscripci\u00f3n y la elecci\u00f3n de escuela en las escuelas p\u00fablicas.", duration: "6 lecciones" },
    { icon: BookOpen, img: COURSE_IMG_2, title: "Apoyando la Lectura de tu Hijo", desc: "H\u00e1bitos diarios sencillos para desarrollar buenas habilidades de lectura en casa.", duration: "4 lecciones" },
    { icon: Laptop, img: COURSE_IMG_3, title: "Navegando el Portal para Padres", desc: "Configura y usa HISD Connect para ver calificaciones y asistencia.", duration: "30 min" },
    { icon: ShieldCheck, img: COURSE_IMG_4, title: "Seguridad Digital para Familias", desc: "Mant\u00e9n a tus hijos seguros en l\u00ednea con consejos pr\u00e1cticos y f\u00e1ciles.", duration: "5 lecciones" },
    { icon: Languages, img: COURSE_IMG_5, title: "Ingl\u00e9s para Padres (ESL B\u00e1sico)", desc: "Ingl\u00e9s cotidiano para comunicarte con maestros y personal.", duration: "8 lecciones" },
    { icon: HeartPulse, img: COURSE_IMG_6, title: "Apoyando el Bienestar de tu Hijo", desc: "Reconoce el estr\u00e9s y cuida la salud mental de tu hijo.", duration: "4 lecciones" },
  ],
};

const SOCIAL = {
  en: [
    { icon: UtensilsCrossed, title: "Food Assistance", desc: "Food pantries, SNAP benefits and free meal programs for families in need.", cls: "text-primary bg-primary/10" },
    { icon: Building2, title: "Housing & Shelter", desc: "Emergency shelter, rent help and support for families facing housing instability.", cls: "text-accent bg-accent/10" },
    { icon: HeartPulse, title: "Health & Mental Health", desc: "Free and low-cost clinics, counseling and mental health support.", cls: "text-secondary bg-secondary/15" },
    { icon: Scale, title: "Immigration & Legal Aid", desc: "Free legal guidance, know-your-rights information and immigration support.", cls: "text-primary bg-primary/10" },
    { icon: HandCoins, title: "Financial & Utility Help", desc: "Assistance with utility bills, childcare costs and emergency funds.", cls: "text-accent bg-accent/10" },
    { icon: HeartHandshake, title: "Family Counseling", desc: "Caring counselors and family support services, offered with dignity.", cls: "text-secondary bg-secondary/15" },
  ],
  es: [
    { icon: UtensilsCrossed, title: "Ayuda Alimentaria", desc: "Despensas de comida, beneficios de SNAP y programas de comidas gratis para familias necesitadas.", cls: "text-primary bg-primary/10" },
    { icon: Building2, title: "Vivienda y Refugio", desc: "Refugio de emergencia, ayuda con la renta y apoyo para familias con inestabilidad de vivienda.", cls: "text-accent bg-accent/10" },
    { icon: HeartPulse, title: "Salud y Salud Mental", desc: "Cl\u00ednicas gratuitas o de bajo costo, consejer\u00eda y apoyo de salud mental.", cls: "text-secondary bg-secondary/15" },
    { icon: Scale, title: "Inmigraci\u00f3n y Ayuda Legal", desc: "Orientaci\u00f3n legal gratuita, informaci\u00f3n sobre tus derechos y apoyo migratorio.", cls: "text-primary bg-primary/10" },
    { icon: HandCoins, title: "Ayuda Financiera y de Servicios", desc: "Asistencia con facturas de servicios, cuidado infantil y fondos de emergencia.", cls: "text-accent bg-accent/10" },
    { icon: HeartHandshake, title: "Consejer\u00eda Familiar", desc: "Consejeros atentos y servicios de apoyo familiar, ofrecidos con dignidad.", cls: "text-secondary bg-secondary/15" },
  ],
};

export default function Home() {
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, [location]);

  const helperTarget = user ? "/dashboard" : "/login";

  return (
    <div className="App min-h-screen bg-background">
      <Header />

      {/* Hero — Ask the HISD Family Helper (main feature) */}
      <section id="chat" className="relative overflow-hidden scroll-mt-24">
        <div className="grain-bg absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-7xl px-5 pb-6 pt-12 lg:px-8 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-3xl text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-primary">
              <Sparkles className="h-4 w-4" strokeWidth={2} /> {t("chat_eyebrow")}
            </span>
            <h1 className="mt-5 font-heading text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t("chat_title")}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {t("hero_subtitle")}
            </p>
          </motion.div>

          {/* Spanish emphasis banner — moved up under the headline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="relative mt-8 overflow-hidden rounded-3xl bg-primary px-7 py-6 text-primary-foreground sm:px-10 sm:py-7"
            data-testid="spanish-banner"
          >
            <div className="relative z-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary-foreground/80">
                  <span className="rounded-full bg-white/20 px-2.5 py-0.5">ES</span> Para familias hispanohablantes
                </div>
                <h2 className="mt-2 font-heading text-2xl font-semibold sm:text-3xl">{t("es_banner_title")}</h2>
                <p className="mt-1.5 text-base leading-relaxed text-primary-foreground/90">{t("es_banner_text")}</p>
              </div>
              <Button
                data-testid="spanish-banner-btn"
                onClick={() => setLang("es")}
                className="h-12 shrink-0 rounded-full bg-white px-7 text-base font-semibold text-primary hover:bg-white/90"
              >
                {t("es_banner_cta")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
            <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8 grid gap-6 lg:grid-cols-12"
          >
            <div className="lg:col-span-7">
              <ChatInterface />
              <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="trust-chips">
                {[
                  { icon: BadgeCheck, label: t("trust_free") },
                  { icon: Lock, label: t("trust_private") },
                  { icon: Clock, label: t("trust_247") },
                  { icon: Languages, label: t("trust_bilingual") },
                ].map((c, i) => (
                  <span
                    key={i}
                    data-testid={`trust-chip-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium text-foreground/80"
                  >
                    <c.icon className="h-4 w-4 text-primary" strokeWidth={2} /> {c.label}
                  </span>
                ))}
              </div>
              <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <Headphones className="h-4 w-4 text-accent" strokeWidth={1.75} />
                {t("helper_inline")}{" "}
                <Link to={helperTarget} data-testid="helper-inline-link" className="font-semibold text-primary hover:underline">
                  {user ? t("helper_cta") : t("helper_signin")}
                </Link>
              </p>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="relative h-full min-h-[340px] overflow-hidden rounded-3xl border border-black/5 shadow-[0_30px_80px_-40px_rgba(43,45,66,0.5)]"
                data-testid="family-photo"
              >
                <img src={HISPANIC_IMG} alt="Houston ISD mother and daughter" className="absolute inset-0 h-full w-full object-cover object-[35%_center]" />
                <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-2xl border border-black/5 bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/15 text-secondary">
                    <HeartHandshake className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <p className="text-xs text-muted-foreground">Houston ISD</p>
                    <p className="font-heading text-sm font-semibold text-foreground">180,000+ familias</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events & Important Dates */}
      <section id="events" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent/10 text-accent">
            <CalendarDays className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t("events_title")}</h2>
            <p className="mt-1 text-base text-muted-foreground">{t("events_subtitle")}</p>
          </div>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="events-list">
          {(EVENTS[lang] || EVENTS.en).map((ev, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="flex items-center gap-5 rounded-3xl border border-black/5 bg-card p-5 shadow-sm"
              data-testid={`event-card-${i}`}
            >
              <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <span className="text-xs font-semibold uppercase tracking-wide">{ev.month}</span>
                <span className="font-heading text-2xl font-semibold leading-none">{ev.day}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg font-semibold text-foreground">{ev.title}</h3>
                  <span className="rounded-full bg-surfaceAlt px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">{ev.tag}</span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{ev.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Free Online Tutor — full-width row */}
      <section id="tutor" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid overflow-hidden rounded-[2rem] border border-black/5 bg-secondary shadow-[0_30px_80px_-40px_rgba(43,45,66,0.4)] lg:grid-cols-2"
          data-testid="tutor-section"
        >
          <div className="flex flex-col justify-center p-9 text-secondary-foreground sm:p-12">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">
              <BookOpen className="h-3.5 w-3.5" strokeWidth={2} /> {t("tutor_eyebrow")}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-tight sm:text-4xl">{t("tutor_title")}</h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-secondary-foreground/90">{t("tutor_text")}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                data-testid="tutor-cta-btn"
                disabled
                className="h-12 rounded-full bg-white px-7 text-base font-semibold text-secondary hover:bg-white disabled:opacity-100"
              >
                <Laptop className="mr-2 h-4 w-4" strokeWidth={2} /> {t("tutor_badge")}
              </Button>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-2 text-sm font-medium">
                <Sparkles className="h-4 w-4" strokeWidth={1.75} /> {t("tutor_credits_note")}
              </span>
            </div>
          </div>
          <div className="relative min-h-[280px] lg:min-h-full">
            <img
              src={TUTOR_IMG}
              alt="Child learning online with a tutor"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* Free Resources for Parents */}
      <section id="resources" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-secondary/15 text-secondary">
            <BookOpen className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t("resources_title")}</h2>
            <p className="mt-1 text-base text-muted-foreground">{t("resources_subtitle")}</p>
          </div>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="resources-list">
          {(RESOURCES[lang] || RESOURCES.en).map((r, i) => (
            <motion.a
              key={i}
              href="#"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group flex flex-col rounded-3xl border border-black/5 bg-card p-7 shadow-sm transition-colors hover:border-primary/40"
              data-testid={`resource-card-${i}`}
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary">
                <r.icon className="h-6 w-6" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground">{r.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{r.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                {t("view_resource")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </span>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Free Online Courses for Parents */}
      <section id="courses" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary">
            <GraduationCap className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t("courses_title")}</h2>
            <p className="mt-1 text-base text-muted-foreground">{t("courses_subtitle")}</p>
          </div>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="courses-list">
          {(COURSES[lang] || COURSES.en).map((c, i) => (
            <motion.a
              key={i}
              href="#"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-card shadow-sm transition-colors hover:border-primary/40"
              data-testid={`course-card-${i}`}
            >
              <div className="relative h-40 overflow-hidden">
                <img src={c.img} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-3 top-3 grid h-10 w-10 place-items-center rounded-xl bg-background/95 text-primary shadow-sm backdrop-blur">
                  <c.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold text-muted-foreground shadow-sm backdrop-blur">
                  <Clock className="h-3.5 w-3.5" strokeWidth={2} /> {c.duration}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  <PlayCircle className="h-4 w-4" strokeWidth={2} /> {t("course_start")}
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Free Social Services & Resources */}
      <section id="social-services" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <div className="rounded-[2rem] bg-surfaceAlt p-8 sm:p-12">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-foreground">
              <LifeBuoy className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t("social_title")}</h2>
              <p className="mt-1 text-base text-muted-foreground">{t("social_subtitle")}</p>
            </div>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="social-list">
            {(SOCIAL[lang] || SOCIAL.en).map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex flex-col rounded-3xl border border-black/5 bg-card p-7 shadow-sm"
                data-testid={`social-card-${i}`}
              >
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${s.cls}`}>
                  <s.icon className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-start gap-4 rounded-3xl bg-accent p-6 text-accent-foreground sm:flex-row sm:items-center sm:justify-between" data-testid="social-helpline">
            <div className="flex items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/15">
                <Phone className="h-6 w-6" strokeWidth={1.75} />
              </span>
              <div>
                <h3 className="font-heading text-lg font-semibold">{t("social_help_title")}</h3>
                <p className="mt-1 text-sm text-accent-foreground/85">{t("social_help_text")}</p>
              </div>
            </div>
            <Button asChild className="shrink-0 rounded-full bg-white font-semibold text-accent hover:bg-white/90" data-testid="social-cta-btn">
              <Link to={helperTarget}>{t("social_cta")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section id="news" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Newspaper className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t("news_title")}</h2>
            <p className="mt-1 text-base text-muted-foreground">{t("news_subtitle")}</p>
          </div>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="news-list">
          {(NEWS[lang] || NEWS.en).map((n, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-card shadow-sm"
              data-testid={`news-card-${i}`}
            >
              <div className="relative h-44 overflow-hidden">
                <img src={n.img} alt={n.title} className="h-full w-full object-cover" />
                <span className="absolute left-4 top-4 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-accent backdrop-blur">
                  {n.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{n.date}</p>
                <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-foreground">{n.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{n.excerpt}</p>
                <a href="#" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                  {t("read_more")} <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
